import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

@observer
class Pagination extends Component {
    constructor(props) {
        super(props);
        this.state = { gotoValue: '' };
        this.onKeyDown = this.onKeyDown.bind(this);
    }

    static defaultProps = {
        windowSize: 3,          // how many pages to show around current
        jumpStep: 10,           // how many pages to jump with ± buttons / Shift+Arrows
        pageSizeOptions: [10, 25, 50, 100],
        enableKeyboardShortcuts: true
    };

    componentDidMount() {
        if (this.props.enableKeyboardShortcuts) {
            window.addEventListener('keydown', this.onKeyDown);
        }
        this.prefetchNeighbors();
    }

    componentWillUnmount() {
        if (this.props.enableKeyboardShortcuts) {
            window.removeEventListener('keydown', this.onKeyDown);
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.current !== this.props.current || prevProps.limit !== this.props.limit || prevProps.total !== this.props.total) {
            this.prefetchNeighbors();
        }
    }

    getTotalPages() {
        const { total = 0, limit = 1 } = this.props;
        return Math.max(1, Math.ceil(total / Math.max(1, limit)));
    }

    clamp(p) {
        return Math.min(this.getTotalPages(), Math.max(1, p));
    }

    goTo = (p) => {
        this.props.onPageChange && this.props.onPageChange(this.clamp(p));
    }

    prefetchNeighbors() {
        const { onPrefetch } = this.props;
        if (!onPrefetch) return;
        const totalPages = this.getTotalPages();
        const { current } = this.props;
        const next = Math.min(totalPages, current + 1);
        const prev = Math.max(1, current - 1);
        if (typeof requestIdleCallback === 'function') {
            requestIdleCallback(() => {
                if (next !== current) onPrefetch(next);
                if (prev !== current) onPrefetch(prev);
            });
        }
    }

    onKeyDown(e) {
        const tag = (e.target && e.target.tagName) || '';
        if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target && e.target.isContentEditable)) return;

        const { current, jumpStep } = { current: this.props.current, jumpStep: this.props.jumpStep };
        if (e.key === 'ArrowLeft' && e.shiftKey) this.goTo(current - jumpStep);
        else if (e.key === 'ArrowRight' && e.shiftKey) this.goTo(current + jumpStep);
        else if (e.key === 'ArrowLeft') this.goTo(current - 1);
        else if (e.key === 'ArrowRight') this.goTo(current + 1);
        else if (e.key === 'Home') this.goTo(1);
        else if (e.key === 'End') this.goTo(this.getTotalPages());
    }

    renderPageNumbers(total, limit, current, classNames) {
        const totalPages = Math.ceil(total / limit) || 1;
        const { windowSize } = this.props;

        const set = new Set([1, totalPages]);
        for (let i = current - windowSize; i <= current + windowSize; i++) {
            if (i >= 1 && i <= totalPages) set.add(i);
        }
        const arr = [...set].sort((a, b) => a - b);

        const nodes = [];
        for (let i = 0; i < arr.length; i++) {
            const p = arr[i];
            nodes.push(
                <li class={`page-item ${p === current ? 'active' : ''}`} key={`p-${p}`}>
                    <button type='button' class={`page-link ${classNames}`} onClick={() => this.goTo(p)} aria-current={p === current ? 'page' : undefined}>
                        {p}
                    </button>
                </li>
            );
            if (i < arr.length - 1 && arr[i + 1] - p > 1) {
                nodes.push(<li key={`e-${p}`} class={`page-item disabled`}><span class={`page-link ${classNames}`}>…</span></li>);
            }
        }
        return nodes;
    }

    render() {
        const { total = 0, limit = 10, current = 1, jumpStep, pageSizeOptions, onPageSizeChange, minimalistic } = this.props;
        const totalPages = this.getTotalPages();

        const from = total ? (current - 1) * limit + 1 : 0;
        const to = Math.min(total, current * limit);

        if (minimalistic) {
            return (
                <div class='d-flex justify-content-center align-items-center'>
                    <nav aria-label='Pagination'>
                        <ul class='pagination mb-0'>
                            {/* First / Prev / -jump */}
                            <li class={`page-item me-1 ${current === 1 ? 'disabled' : ''}`}>
                                <button type='button' class='page-link py-0' onClick={() => this.goTo(1)} title='First'>«</button>
                            </li>
                            <li class={`page-item me-1 ${current === 1 ? 'disabled' : ''}`}>
                                <button type='button' class='page-link py-0' onClick={() => this.goTo(current - 1)} title='Prev'>‹</button>
                            </li>

                            {/* Numbered window with ellipses */}
                            {this.renderPageNumbers(total, limit, current, 'py-0')}

                            <li class={`page-item ms-1 ${current === totalPages ? 'disabled' : ''}`}>
                                <button type='button' class='page-link py-0' onClick={() => this.goTo(current + 1)} title='Next'>›</button>
                            </li>
                            <li class={`page-item ms-1 ${current === totalPages ? 'disabled' : ''}`}>
                                <button type='button' class='page-link py-0' onClick={() => this.goTo(totalPages)} title='Last'>»</button>
                            </li>
                        </ul>
                    </nav>
                </div>
            );
        }

        return (
            <div class='d-flex flex-column justify-content-center align-items-center mt-3'>
                <nav aria-label='Pagination'>
                    <ul class='pagination mb-2'>
                        {/* First / Prev / -jump */}
                        <li class={`page-item me-1 ${current === 1 ? 'disabled' : ''}`}>
                            <button type='button' class='page-link' onClick={() => this.goTo(1)} title='First'>«</button>
                        </li>
                        <li class={`page-item me-1 ${current === 1 ? 'disabled' : ''}`}>
                            <button type='button' class='page-link' onClick={() => this.goTo(current - 1)} title='Prev'>‹</button>
                        </li>
                        <li class={`page-item me-2 ${current === 1 ? 'disabled' : ''}`}>
                            <button type='button' class='page-link' onClick={() => this.goTo(current - jumpStep)} title={`-${jumpStep}`}>-{jumpStep}</button>
                        </li>

                        {/* Numbered window with ellipses */}
                        {this.renderPageNumbers(total, limit, current)}

                        {/* +jump / Next / Last */}
                        <li class={`page-item ms-2 ${current === totalPages ? 'disabled' : ''}`}>
                            <button type='button' class='page-link' onClick={() => this.goTo(current + jumpStep)} title={`+${jumpStep}`}>+{jumpStep}</button>
                        </li>
                        <li class={`page-item ms-1 ${current === totalPages ? 'disabled' : ''}`}>
                            <button type='button' class='page-link' onClick={() => this.goTo(current + 1)} title='Next'>›</button>
                        </li>
                        <li class={`page-item ms-1 ${current === totalPages ? 'disabled' : ''}`}>
                            <button type='button' class='page-link' onClick={() => this.goTo(totalPages)} title='Last'>»</button>
                        </li>
                    </ul>
                </nav>

                {/* Go to page + page size */}
                <div class='d-flex flex-nowrap align-items-center mb-2'>
                    <label class='me-2 mb-0 text-nowrap'>Go to</label>
                    <input
                        type='number'
                        inputMode='numeric'
                        min='1'
                        max={totalPages}
                        class='form-control form-control-sm'
                        style='width: 7rem;'
                        value={this.state.gotoValue}
                        placeholder={`${current}/${totalPages}`}
                        onInput={(e) => this.setState({ gotoValue: e.currentTarget.value })}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && this.state.gotoValue) {
                                this.goTo(parseInt(this.state.gotoValue, 10));
                                this.setState({ gotoValue: '' });
                            }
                        }}
                    />
                    {onPageSizeChange && (
                        <>
                            <label class='ms-3 me-2 mb-0 text-nowrap'>Per page</label>
                            <select
                                class='form-control form-control-sm'
                                value={limit}
                                onChange={(e) => onPageSizeChange(parseInt(e.currentTarget.value, 10))}
                            >
                                {pageSizeOptions.map((sz) => (
                                    <option key={sz} value={sz}>{sz}</option>
                                ))}
                            </select>
                        </>
                    )}
                </div>

                <small class='text-muted'>
                    <Text id='pagination.page'>Page</Text> {current} <Text id='pagination.of'>of</Text> {totalPages}
                </small>
                <small class='text-muted font-weight-lighter'>
                    <Text id='pagination.total'>Total</Text> {total} {total ? `• Showing ${from}-${to}` : ''}
                </small>
            </div>
        );
    }
}

export default Pagination;
